// ── STAFFING BAR FILL ────────────────────────────────────────────────────────
// One source for how a staffing bar is coloured and how wide it is drawn.
//
// There were four copies of this logic — StaffingBar and RoleStaffingBar in
// AgentDashboard.js, the per-day series bar and the event card bar in
// AgentMobileDashboard.js — and they had drifted into two different rules.
// Two tested a percentage (`pct === 100`), two tested the counts
// (`filled >= total`). A 2/3 role rendered RED directly above the same event's
// AMBER 67% bar, and 2/3 read identically to 0/1.
//
// Green is decided on the COUNTS, never on the percentage. `pct === 100` is a
// rounding artefact: 199/200 is 99.5%, which Math.round takes to 100, so a bar
// one person short reported itself full. Over-staffing broke it the other way —
// 3 filled against 2 spots is 150%, which is not 100, so an over-full event
// went red on mobile while the desktop bar clamped to 100 and went green.
//
// What the colours mean to a coordinator:
//   green  — fully staffed, ready to run
//   amber  — nearly there
//   red    — start accepting applicants or messaging staff
//
// AMBER_FROM_PCT is the one number to change if amber should start earlier or
// later. It is deliberately a single constant in a single file: retuning it is
// now one line, where it used to be a four-site hunt.

export const AMBER_FROM_PCT = 60;

// Percentage filled, rounded and clamped to 0-100. Clamping matters: without it
// an over-staffed role draws a bar wider than its own container.
export function fillPercent(filled, total) {
  const f = Number(filled) || 0;
  const t = Number(total) || 0;
  if (t <= 0) return 0;
  return Math.min(100, Math.round((f / t) * 100));
}

// True only when the roster is genuinely complete. Use this for "Fully staffed"
// text and green count labels so the words and the bar can never disagree.
export function isFullyStaffed(filled, total) {
  const f = Number(filled) || 0;
  const t = Number(total) || 0;
  return t > 0 && f >= t;
}

// The bar colour. Takes counts, not a percentage.
// total <= 0 returns danger: a bar with no spots defined is a configuration
// problem worth flagging, not something to call ready.
export function fillColor(filled, total) {
  const t = Number(total) || 0;
  if (t <= 0) return 'var(--danger)';
  if (isFullyStaffed(filled, total)) return 'var(--green)';
  return fillPercent(filled, total) >= AMBER_FROM_PCT ? 'var(--amber)' : 'var(--danger)';
}
